import { Helmet } from "react-helmet-async";

type SEOProps = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  schema?: Record<string, any> | Record<string, any>[];
  keywords?: string;
  publishedTime?: string;
  modifiedTime?: string;
};

export const SEO = ({ 
  title, 
  description, 
  path = "/",
  image = "/og-image.png",
  type = "website",
  schema,
  keywords,
  publishedTime,
  modifiedTime,
}: SEOProps) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${origin}${image}`;

  // Default schema for website
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Medical Exam Prep Platform",
    "url": origin,
    "description": "Comprehensive medical exam preparation platform with MCQs, study notes, and mock exams",
    "sameAs": []
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Medical Exam Prep Platform" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Article Meta Tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* JSON-LD Structured Data */}
      {schema && (
        Array.isArray(schema) ? (
          schema.map((s, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(s)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )
      )}
      {!schema && (
        <script type="application/ld+json">
          {JSON.stringify(defaultSchema)}
        </script>
      )}
    </Helmet>
  );
};
